import { useState, useEffect, useRef, useMemo } from 'react';
import { Calendar, ChevronDown, Clock, DollarSign, Edit3, Plus } from 'lucide-react';
import { format, startOfMonth, endOfMonth, subMonths, subDays, startOfDay, endOfDay, parseISO, isValid } from 'date-fns';
import { useBusiness } from '../contexts/BusinessContext';
import { usePayrollHoursText } from '../hooks/usePayrollHoursText';
import { getPayrollEntries } from '../lib/payroll';
import { createPayrollRequest, getPayrollRequestsForResource } from '../lib/payrollRequests';
import PayrollChangeRequestModal from './PayrollChangeRequestModal';
import AddMissingEntryModal from './AddMissingEntryModal';
import ChangeRequestBadge from './ChangeRequestBadge';
import RequestFlatRateEntriesModal from './RequestFlatRateEntriesModal';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import { 
  getPayrollPeriodSettings, 
  getCurrentPeriodRange as getPayrollCurrentPeriod, 
  getLastPeriodRange as getPayrollLastPeriod,
  type PayrollPeriodSettings 
} from '../lib/payrollPeriodSettings';

interface PayrollEntry {
  id: string;
  resource_id: string;
  resource_first_name: string;
  resource_last_name: string;
  resource_email: string;
  resource_avatar_url?: string;
  compensation_type: string;
  flat_rate_amount: number | null;
  hourly_rate: number | null;
  booking_id: string | null;
  booking_number: number | null;
  booking_title: string | null;
  start_date: string | null;
  end_date: string | null;
  entry_type: 'flat_rate' | 'timeclock' | 'commission' | 'bonus';
  hours: number;
  earnings: number;
  item_name: string | null;
  item_description: string | null;
  created_at: string;
  clock_in?: string;
  clock_out?: string;
  break_start?: string;
  break_end?: string;
  total_minutes?: number;
  notes?: string;
  status?: string;
  process_status?: string;
  completed_at?: string;
}

interface UserInfo {
  resourceId?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  avatarUrl?: string;
}

interface EarningsReportProps {
  userInfo: UserInfo;
}

// Helper functions

const formatDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  if (!isValid(date)) return '-';
  return format(date, 'MMM d, yyyy');
};

export default function EarningsReport({ userInfo }: EarningsReportProps) {
  const { selectedBusiness } = useBusiness();
  const [entries, setEntries] = useState<PayrollEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [isCustomRangePickerOpen, setIsCustomRangePickerOpen] = useState(false);
  const [customStartDate, setCustomStartDate] = useState<Date | null>(null);
  const [customEndDate, setCustomEndDate] = useState<Date | null>(null);
  const [expandedBookings, setExpandedBookings] = useState<Set<string>>(new Set());
  const [payrollSettings, setPayrollSettings] = useState<PayrollPeriodSettings | null>(null);

  // Dynamic DATE_RANGES based on payroll settings (same as ManagePayroll)
  const DATE_RANGES = useMemo(() => {
    const standardRanges = [
      { label: 'Today', value: 'today', getRange: () => {
        const today = new Date();
        return { start: startOfDay(today), end: endOfDay(today) };
      }},
      { label: 'Yesterday', value: 'yesterday', getRange: () => {
        const yesterday = subDays(new Date(), 1);
        return { start: startOfDay(yesterday), end: endOfDay(yesterday) };
      }},
      { label: 'Select Day', value: 'select_day', getRange: (selectedDay?: Date) => {
        const day = selectedDay || new Date();
        return { start: startOfDay(day), end: endOfDay(day) };
      }},
      { label: 'Last 30 days', value: 'last_30_days', getRange: () => ({
        start: subDays(new Date(), 29),
        end: new Date()
      })},
      { label: 'This Month', value: 'this_month', getRange: () => ({
        start: startOfMonth(new Date()),
        end: endOfMonth(new Date())
      })},
      { label: 'Last Month', value: 'last_month', getRange: () => {
        const lastMonth = subMonths(new Date(), 1);
        return { start: startOfMonth(lastMonth), end: endOfMonth(lastMonth) };
      }},
      { label: 'Custom Range', value: 'custom_range', getRange: (customStartDate?: Date, customEndDate?: Date) => ({
        start: customStartDate || startOfMonth(new Date()),
        end: customEndDate || endOfMonth(new Date())
      })}
    ];

    // Add period-based ranges if settings exist
    if (payrollSettings) {
      return [
        { 
          label: 'Current Period', 
          value: 'current_period', 
          getRange: () => getPayrollCurrentPeriod(payrollSettings)
        },
        { 
          label: 'Last Period', 
          value: 'last_period', 
          getRange: () => getPayrollLastPeriod(payrollSettings)
        },
        ...standardRanges
      ];
    }

    return standardRanges;
  }, [payrollSettings]);

  // Selected range and date range states (must come after DATE_RANGES)
  const [selectedRange, setSelectedRange] = useState(
    DATE_RANGES.find(r => r.value === (payrollSettings ? 'current_period' : 'this_month'))!
  );
  const [dateRange, setDateRange] = useState(selectedRange.getRange());
  const [isExpanded, setIsExpanded] = useState(false);
  const dropdownContainerRef = useRef<HTMLDivElement>(null);

  // Get resource info from first entry or from userInfo
  const [resourceInfo, setResourceInfo] = useState<{
    first_name: string;
    last_name: string;
    email: string;
    compensation_type: string;
    flat_rate_amount: number | null;
    hourly_rate: number | null;
    avatar_url?: string;
  } | null>(null);

  // Change request modal state
  const [isChangeRequestModalOpen, setIsChangeRequestModalOpen] = useState(false);
  const [requestingEntry, setRequestingEntry] = useState<PayrollEntry | null>(null);
  
  // Add missing entry modal state
  const [isAddMissingEntryModalOpen, setIsAddMissingEntryModalOpen] = useState(false);
  
  // Flat rate request modal state
  const [isFlatRateRequestModalOpen, setIsFlatRateRequestModalOpen] = useState(false);
  const [requestingBooking, setRequestingBooking] = useState<{ id: string; number: number; title: string } | null>(null);
  
  // Track pending change requests for entries
  const [pendingRequests, setPendingRequests] = useState<Record<string, any>>({});


  // Get resource info when userInfo changes
  useEffect(() => {
    if (userInfo.resourceId) {
      loadResourceInfo();
    }
  }, [userInfo.resourceId]);

  // Load payroll settings when business changes
  useEffect(() => {
    const loadSettings = async () => {
      if (!selectedBusiness?.id) return;
      
      try {
        const settings = await getPayrollPeriodSettings(selectedBusiness.id);
        setPayrollSettings(settings);
      } catch (error) {
        console.error('Error loading payroll settings:', error);
        // Non-critical error, continue without settings
      }
    };

    loadSettings();
  }, [selectedBusiness?.id]);

  // Update selected range when payroll settings change
  useEffect(() => {
    if (payrollSettings) {
      const currentPeriodRange = DATE_RANGES.find(r => r.value === 'current_period');
      if (currentPeriodRange) {
        setSelectedRange(currentPeriodRange);
        // Also immediately update the date range
        setDateRange(currentPeriodRange.getRange());
      }
    }
  }, [payrollSettings]);

  // Update date range when selection changes
  useEffect(() => {
    if (selectedRange.value === 'select_day') {
      setDateRange(selectedRange.getRange(selectedDay || new Date()));
    } else if (selectedRange.value === 'custom_range') {
      setDateRange(selectedRange.getRange(customStartDate || undefined, customEndDate || undefined));
    } else {
      setDateRange(selectedRange.getRange());
    }
  }, [selectedRange, selectedDay, customStartDate, customEndDate]);

  // Load entries when date range changes
  useEffect(() => {
    loadEntries();
  }, [dateRange, selectedBusiness?.id, userInfo.resourceId]);

  const loadResourceInfo = async () => {
    if (!userInfo.resourceId || !selectedBusiness?.id || !supabase) return;

    try {
      const { data, error } = await supabase
        .from('resources')
        .select('first_name, last_name, email, compensation_type, flat_rate_amount, hourly_rate, avatar_url')
        .eq('id', userInfo.resourceId)
        .eq('business_id', selectedBusiness.id)
        .single();

      if (error) throw error;
      if (data) {
        setResourceInfo(data);
      }
    } catch (err) {
      console.error('Error loading resource info:', err);
    }
  };


  const loadEntries = async () => {
    if (!selectedBusiness?.id || !userInfo.resourceId) return;

    try {
      setLoading(true);
      const data = await getPayrollEntries(
        selectedBusiness.id,
        format(dateRange.start, 'yyyy-MM-dd'),
        format(dateRange.end, 'yyyy-MM-dd')
      );
      
      // Filter entries to only show those for the logged-in resource
      const filteredEntries = data.filter(entry => entry.resource_id === userInfo.resourceId);
      setEntries(filteredEntries as PayrollEntry[]);
      
      // Load pending change requests for these entries
      await loadPendingRequests();
    } catch (err) {
      console.error('Error loading earnings entries:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadPendingRequests = async () => {
    if (!selectedBusiness?.id || !userInfo.resourceId) return;

    try {
      const requests = await getPayrollRequestsForResource(userInfo.resourceId, selectedBusiness.id);
      
      // Create a mapping of payroll_entry_id -> request for pending requests
      // Only include change requests (not add requests) since add requests don't have payroll_entry_id
      const requestMap = requests
        .filter(request => request.status === 'pending' && request.request_type === 'change' && request.payroll_entry_id)
        .reduce((acc, request) => {
          if (request.payroll_entry_id) {
            acc[request.payroll_entry_id] = request;
          }
          return acc;
        }, {} as Record<string, any>);
      
      setPendingRequests(requestMap);
      console.log('Loaded pending requests:', requestMap);
    } catch (err) {
      console.error('Error loading pending requests:', err);
    }
  };

  const toggleBooking = (bookingId: string) => {
    setExpandedBookings(prev => {
      const next = new Set(prev);
      if (next.has(bookingId)) {
        next.delete(bookingId);
      } else {
        next.add(bookingId);
      }
      return next;
    });
  };

  // Change request handlers
  const handleRequestChange = (entry: PayrollEntry) => {
    setRequestingEntry({
      ...entry,
      hourly_rate: entry.hourly_rate || resourceInfo?.hourly_rate || 0
    });
    setIsChangeRequestModalOpen(true);
  };

  const handleSubmitChangeRequest = async (requestData: any) => {
    if (!selectedBusiness?.id || !userInfo.resourceId) return;

    try {
      await createPayrollRequest({
        ...requestData,
        business_id: selectedBusiness.id,
        resource_id: userInfo.resourceId,
        request_type: 'change'
      });
      
      toast.success('Change request submitted successfully! It will be reviewed by management.');
      setIsChangeRequestModalOpen(false);
      setRequestingEntry(null);
      
      // Reload pending requests to show the new badge
      await loadPendingRequests();
    } catch (error) {
      console.error('Error submitting change request:', error);
      throw error; // Re-throw to let modal handle the error display
    }
  };

  const handleCloseChangeRequestModal = () => {
    setIsChangeRequestModalOpen(false);
    setRequestingEntry(null);
  };

  const handleAddMissingEntry = () => {
    setIsAddMissingEntryModalOpen(true);
  };

  const handleAddMissingEntrySuccess = async () => {
    await loadEntries(); // Reload entries
    await loadPendingRequests(); // Reload pending requests
  };

  const handleCloseAddMissingEntryModal = () => {
    setIsAddMissingEntryModalOpen(false);
  };

  const handleRequestFlatRateEntries = (bookingId: string, bookingNumber: number, bookingTitle: string) => {
    setRequestingBooking({ id: bookingId, number: bookingNumber, title: bookingTitle });
    setIsFlatRateRequestModalOpen(true);
  };

  const handleCloseFlatRateRequestModal = () => {
    setIsFlatRateRequestModalOpen(false);
    setRequestingBooking(null);
  };

  const handleFlatRateRequestSuccess = () => {
    loadPendingRequests(); // Reload pending requests to show new flat rate requests
    loadEntries(); // Reload data to refresh the view
  };

  const handleDateRangeToggle = () => {
    setIsDateRangeOpen(!isDateRangeOpen);
    setIsDatePickerOpen(false);
    setIsCustomRangePickerOpen(false);
  };

  useEffect(() => {
    if (!isDateRangeOpen && !isDatePickerOpen && !isCustomRangePickerOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownContainerRef.current && !dropdownContainerRef.current.contains(event.target as Node)) {
        setIsDateRangeOpen(false);
        setIsDatePickerOpen(false);
        setIsCustomRangePickerOpen(false);
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsDateRangeOpen(false);
        setIsDatePickerOpen(false);
        setIsCustomRangePickerOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isDateRangeOpen, isDatePickerOpen, isCustomRangePickerOpen]);

  // Group entries by type and booking (exactly like PayrollResourceSummary)
  const entriesData = entries.reduce<{
    timeclock: {
      entries: PayrollEntry[];
      totalHours: number;
      totalEarnings: number;
    };
    bonuses: {
      entries: PayrollEntry[];
      totalEarnings: number;
    };
    bookings: Record<string, {
      booking_number: number;
      booking_title: string;
      start_date: string;
      end_date: string;
      items: PayrollEntry[];
      totalHours: number;
      totalEarnings: number;
    }>;
    totalHours: number;
    totalEarnings: number;
  }>((acc, entry) => {
    if (entry.entry_type === 'timeclock') {
      // Add to timeclock entries
      acc.timeclock.entries.push(entry);
      acc.timeclock.totalHours += entry.hours || 0;
      acc.timeclock.totalEarnings += entry.earnings || 0;
    } else if (entry.entry_type === 'bonus') {
      // Add to bonus entries
      acc.bonuses.entries.push(entry);
      acc.bonuses.totalEarnings += entry.earnings || 0;
    } else {
      // Add to booking entries (flat_rate and commission)
      if (entry.booking_id && !acc.bookings[entry.booking_id]) {
        acc.bookings[entry.booking_id] = {
          booking_number: entry.booking_number || 0,
          booking_title: entry.booking_title || '',
          start_date: entry.start_date || entry.created_at,
          end_date: entry.end_date || entry.created_at,
          items: [],
          totalHours: 0,
          totalEarnings: 0
        };
      }

      if (entry.booking_id) {
        acc.bookings[entry.booking_id].items.push(entry);
        if (entry.entry_type === 'flat_rate') {
          acc.bookings[entry.booking_id].totalHours += entry.hours || 0;
        }
        acc.bookings[entry.booking_id].totalEarnings += entry.earnings || 0;
      }
    }

    // Update totals
    if (entry.entry_type === 'flat_rate') {
      acc.totalHours += entry.hours || 0;
    } else if (entry.entry_type === 'timeclock') {
      acc.totalHours += entry.hours || 0;
    }
    acc.totalEarnings += entry.earnings || 0;

    return acc;
  }, {
    timeclock: { entries: [], totalHours: 0, totalEarnings: 0 },
    bonuses: { entries: [], totalEarnings: 0 },
    bookings: {},
    totalHours: 0,
    totalEarnings: 0
  });

  const timeclockMinutes = entriesData.timeclock.entries.map((entry) => entry.total_minutes || 0);
  const hoursText = usePayrollHoursText([
    ...timeclockMinutes,
    timeclockMinutes.reduce((sum, minutes) => sum + minutes, 0),
  ]);

  if (loading || !resourceInfo) {
    return (
      <div className="bg-white rounded-xl shadow-sm">
        <div className="flex items-center justify-center h-48">
          <div className="text-center">
            <div className="w-6 h-6 border-[3px] border-gray-200 border-t-[#007AFF] rounded-full animate-spin mx-auto"></div>
            <p className="mt-3 text-[15px] text-gray-500">Loading earnings...</p>
          </div>
        </div>
      </div>
    );
  }

  const flatRateTotal = Object.values(entriesData.bookings).reduce((sum, booking) => 
    sum + booking.items.reduce((itemSum, item) => 
      itemSum + (item.entry_type === 'flat_rate' ? item.earnings : 0), 0
    ), 0);
  
  const commissionTotal = Object.values(entriesData.bookings).reduce((sum, booking) => 
    sum + booking.items.reduce((itemSum, item) => 
      itemSum + (item.entry_type === 'commission' ? item.earnings : 0), 0
    ), 0);

  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm" style={{ 
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Arial, sans-serif',
    }}>
      {/* Header: Date Range + Actions */}
      <div className="px-4 py-3.5 border-b border-gray-100">
        <div className="flex items-center justify-between gap-3">
          <div ref={dropdownContainerRef} className="relative flex-1 min-w-0">
            <button
              onClick={handleDateRangeToggle}
              className="flex items-center gap-2 px-3 py-2 text-[15px] bg-[#F2F2F7] rounded-lg active:bg-gray-200 transition-colors min-w-0"
              style={{ minHeight: '36px' }}
            >
              <Calendar className="h-4 w-4 text-[#007AFF] flex-shrink-0" />
              <span className="font-medium text-black truncate">{selectedRange.label}</span>
              <ChevronDown className={`h-4 w-4 text-gray-400 flex-shrink-0 transition-transform ${isDateRangeOpen ? 'rotate-180' : ''}`} />
            </button>

            {/* Inline Dropdown */}
            {isDateRangeOpen && !isDatePickerOpen && !isCustomRangePickerOpen && (
              <div className="absolute top-full left-0 mt-2 w-72 max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-xl py-1 z-50 border border-gray-200">
                {DATE_RANGES.map((range, index) => {
                  const rangeData = range.getRange();
                  const showDates = range.value === 'current_period' || range.value === 'last_period';
                  
                  return (
                    <div key={range.value}>
                      {index > 0 && <div className="h-px bg-gray-100 mx-4" />}
                      <button
                        onClick={() => {
                          setSelectedRange(range);
                          if (range.value === 'select_day') {
                            setIsDatePickerOpen(true);
                          } else if (range.value === 'custom_range') {
                            setIsCustomRangePickerOpen(true);
                          } else {
                            setIsDateRangeOpen(false);
                          }
                        }}
                        className={`w-full text-left px-4 py-3 text-[15px] active:bg-gray-100 transition-colors flex items-center justify-between ${
                          selectedRange.value === range.value ? 'text-[#007AFF] font-medium' : 'text-black'
                        }`}
                      >
                        <span>{range.label}</span>
                        <div className="flex items-center gap-2">
                          {showDates && (
                            <span className="text-[13px] text-gray-400">
                              {format(rangeData.start, 'MMM d')} - {format(rangeData.end, 'MMM d')}
                            </span>
                          )}
                          {selectedRange.value === range.value && (
                            <svg className="w-4 h-4 text-[#007AFF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Inline Date Picker */}
            {isDatePickerOpen && (
              <div className="absolute top-full left-0 mt-2 p-4 bg-white rounded-xl shadow-xl z-50 border border-gray-200">
                <input
                  type="date"
                  value={selectedDay ? format(selectedDay, 'yyyy-MM-dd') : ''}
                  onChange={(e) => {
                    const date = e.target.value ? new Date(e.target.value + 'T00:00:00') : null;
                    setSelectedDay(date);
                    setIsDatePickerOpen(false);
                    setIsDateRangeOpen(false);
                  }}
                  className="w-full px-3 py-2.5 text-[15px] border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#007AFF]/30 focus:border-[#007AFF]"
                  style={{ fontSize: '16px' }}
                />
              </div>
            )}
            
            {/* Inline Custom Range Picker */}
            {isCustomRangePickerOpen && (
              <div className="absolute top-full left-0 mt-2 p-5 bg-white rounded-xl shadow-xl z-50 w-72 max-w-[calc(100vw-2rem)] border border-gray-200">
                <div className="space-y-4">
                  <div>
                    <label className="block text-[13px] font-medium text-gray-500 uppercase tracking-wide mb-1.5">Start Date</label>
                    <input
                      type="date"
                      value={customStartDate ? format(customStartDate, 'yyyy-MM-dd') : ''}
                      onChange={(e) => {
                        const date = e.target.value ? new Date(e.target.value + 'T00:00:00') : null;
                        setCustomStartDate(date);
                      }}
                      className="w-full px-3 py-2.5 text-[15px] border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#007AFF]/30 focus:border-[#007AFF]"
                      style={{ fontSize: '16px' }}
                    />
                  </div>
                  <div>
                    <label className="block text-[13px] font-medium text-gray-500 uppercase tracking-wide mb-1.5">End Date</label>
                    <input
                      type="date"
                      value={customEndDate ? format(customEndDate, 'yyyy-MM-dd') : ''}
                      onChange={(e) => {
                        const date = e.target.value ? new Date(e.target.value + 'T00:00:00') : null;
                        setCustomEndDate(date);
                      }}
                      className="w-full px-3 py-2.5 text-[15px] border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#007AFF]/30 focus:border-[#007AFF]"
                      style={{ fontSize: '16px' }}
                    />
                  </div>
                  <button
                    onClick={() => {
                      setIsCustomRangePickerOpen(false);
                      setIsDateRangeOpen(false);
                    }}
                    className="w-full py-2.5 text-[15px] font-semibold text-white bg-[#007AFF] rounded-lg active:bg-[#0066DD] transition-colors"
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>
          
          {resourceInfo?.compensation_type === 'hourly' && (
            <button
              onClick={handleAddMissingEntry}
              className="flex items-center gap-1.5 px-3 py-2 text-[15px] font-medium text-[#007AFF] active:opacity-60 transition-opacity flex-shrink-0"
              style={{ minHeight: '36px' }}
            >
              <Plus className="h-4 w-4" />
              <span>Add Entry</span>
            </button>
          )}
        </div>
        <p className="text-[13px] text-gray-400 mt-1 px-0.5">
          {format(dateRange.start, 'MMM d, yyyy')} - {format(dateRange.end, 'MMM d, yyyy')}
        </p>
      </div>

      {/* Total Earnings Banner */}
      <div className="px-4 py-4 bg-white">
        <div className="text-center mb-4">
          <p className="text-[13px] text-gray-400 uppercase tracking-wide font-medium">Total Earnings</p>
          <p className="text-[34px] font-bold text-black tracking-tight leading-tight mt-0.5">
            ${entriesData.totalEarnings.toFixed(2)}
          </p>
        </div>

        {/* Stats Grid - iOS style */}
        <div className="grid grid-cols-2 gap-2">
          {entriesData.timeclock.entries.length > 0 && (
            <>
              <div className="bg-[#F2F2F7] rounded-xl p-3">
                <div className="flex items-center gap-1.5 mb-1">
                  <Clock className="h-3.5 w-3.5 text-gray-400" />
                  <span className="text-[12px] text-gray-500 font-medium">Time Clocked</span>
                </div>
                <p className="text-[17px] font-semibold text-black">
                  {hoursText(entriesData.timeclock.entries.reduce((sum, entry) => sum + (entry.total_minutes || 0), 0))}
                </p>
              </div>
              <div className="bg-[#F2F2F7] rounded-xl p-3">
                <div className="flex items-center gap-1.5 mb-1">
                  <DollarSign className="h-3.5 w-3.5 text-gray-400" />
                  <span className="text-[12px] text-gray-500 font-medium">Timeclock</span>
                </div>
                <p className="text-[17px] font-semibold text-black">${entriesData.timeclock.totalEarnings.toFixed(2)}</p>
              </div>
            </>
          )}
          {flatRateTotal > 0 && (
            <div className="bg-[#F2F2F7] rounded-xl p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <DollarSign className="h-3.5 w-3.5 text-gray-400" />
                <span className="text-[12px] text-gray-500 font-medium">Flat Rate</span>
              </div>
              <p className="text-[17px] font-semibold text-black">${flatRateTotal.toFixed(2)}</p>
            </div>
          )}
          {commissionTotal > 0 && (
            <div className="bg-[#F2F2F7] rounded-xl p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <DollarSign className="h-3.5 w-3.5 text-gray-400" />
                <span className="text-[12px] text-gray-500 font-medium">Commission</span>
              </div>
              <p className="text-[17px] font-semibold text-black">${commissionTotal.toFixed(2)}</p>
            </div>
          )}
          {entriesData.bonuses.totalEarnings > 0 && (
            <div className="bg-[#F2F2F7] rounded-xl p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <DollarSign className="h-3.5 w-3.5 text-[#34C759]" />
                <span className="text-[12px] text-gray-500 font-medium">Bonuses</span>
              </div>
              <p className="text-[17px] font-semibold text-black">${entriesData.bonuses.totalEarnings.toFixed(2)}</p>
            </div>
          )}
        </div>
      </div>

      {/* View Details Toggle */}
      <div className="border-t border-gray-100">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-center gap-1 py-3 text-[15px] font-medium text-[#007AFF] active:bg-gray-50 transition-colors"
        >
          <span>{isExpanded ? 'Hide Details' : 'View Details'}</span>
          <ChevronDown className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {/* Expanded Detail Sections */}
      {isExpanded && (
        <div className="border-t border-gray-100">
          {/* Timeclock Entries */}
          {entriesData.timeclock.entries.length > 0 && (
            <div className="px-4 py-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="h-[28px] w-[28px] rounded-[6px] bg-gray-100 flex items-center justify-center">
                    <Clock className="h-[15px] w-[15px] text-gray-600" />
                  </div>
                  <span className="text-[15px] font-semibold text-black">Time Clock</span>
                </div>
                <span className="text-[13px] text-gray-400">
                  {hoursText(entriesData.timeclock.entries.reduce((sum, entry) => sum + (entry.total_minutes || 0), 0))}
                </span>
              </div>
             
              <div className="space-y-2">
                {entriesData.timeclock.entries.map((entry) => (
                  <div key={entry.id} className="bg-[#F2F2F7] rounded-xl p-3.5">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[15px] font-medium text-black">
                            {format(new Date(entry.clock_in || entry.created_at), 'MMM d, yyyy')}
                          </span>
                          <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded-md ${
                            entry.process_status === 'PAID'
                              ? 'bg-green-100 text-green-700'
                              : entry.process_status === 'VOIDED'
                                ? 'bg-red-50 text-red-700'
                                : 'bg-orange-100 text-orange-600'
                          }`}>
                            {entry.process_status === 'PAID' ? 'PAID' : entry.process_status === 'VOIDED' ? 'VOIDED' : 'UNPAID'}
                          </span>
                        </div>
                        <p className="text-[13px] text-gray-500">
                          {entry.clock_in ? format(new Date(entry.clock_in), 'h:mm a') : '-'} - {
                            entry.clock_out ? format(new Date(entry.clock_out), 'h:mm a') : 'In Progress'
                          }
                        </p>
                        {entry.break_start && (
                          <p className="text-[12px] text-gray-400 mt-0.5">
                            Break: {format(new Date(entry.break_start), 'h:mm a')} - {
                              entry.break_end ? format(new Date(entry.break_end), 'h:mm a') : 'In Progress'
                            }
                          </p>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2 ml-3">
                        <div className="text-right">
                          <p className="text-[15px] font-semibold text-black">
                            {hoursText(entry.total_minutes || 0)}
                          </p>
                          <p className="text-[13px] font-medium text-gray-500">
                            ${entry.earnings?.toFixed(2) || '0.00'}
                          </p>
                        </div>
                        
                        {entry.entry_type === 'timeclock' && entry.process_status !== 'PAID' && !pendingRequests[entry.id] && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRequestChange(entry);
                            }}
                            className="p-1.5 text-[#007AFF] active:opacity-50 rounded-lg transition-opacity"
                            title="Request change"
                          >
                            <Edit3 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                    
                    {pendingRequests[entry.id] && (
                      <div className="mt-2">
                        <ChangeRequestBadge 
                          request={pendingRequests[entry.id]}
                          originalEntry={{
                            clock_in: entry.clock_in,
                            clock_out: entry.clock_out,
                            total_minutes: entry.total_minutes
                          }}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Bonus Entries */}
          {entriesData.bonuses.entries.length > 0 && (
            <div className="px-4 py-4 border-t border-gray-100">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="h-[28px] w-[28px] rounded-[6px] bg-green-50 flex items-center justify-center">
                    <DollarSign className="h-[15px] w-[15px] text-[#34C759]" />
                  </div>
                  <span className="text-[15px] font-semibold text-black">Bonuses</span>
                </div>
                <span className="text-[13px] text-gray-400">
                  {entriesData.bonuses.entries.length} bonus{entriesData.bonuses.entries.length !== 1 ? 'es' : ''}
                </span>
              </div>
              
              <div className="space-y-2">
                {entriesData.bonuses.entries.map((entry) => (
                  <div key={entry.id} className="bg-[#F2F2F7] rounded-xl p-3.5">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-[15px] font-medium text-black">
                          {entry.item_name || 'Booking Creation Bonus'}
                        </p>
                        <p className="text-[13px] text-gray-500 mt-0.5">
                          {formatDate(entry.created_at)}
                          {entry.booking_number && ` · Job #${entry.booking_number}`}
                        </p>
                        {entry.item_description && (
                          <p className="text-[12px] text-gray-400 mt-0.5">{entry.item_description}</p>
                        )}
                      </div>
                      <span className="text-[17px] font-semibold text-[#34C759] ml-3">
                        +${(entry.earnings || 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Booking / Job Entries */}
          {Object.entries(entriesData.bookings).map(([bookingId, booking]) => (
            <div key={bookingId} className="border-t border-gray-100">
              <button 
                className="w-full px-4 py-3.5 flex items-center justify-between active:bg-gray-50 transition-colors"
                onClick={() => toggleBooking(bookingId)}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="h-[28px] w-[28px] rounded-[6px] bg-blue-50 flex items-center justify-center flex-shrink-0">
                    <Calendar className="h-[15px] w-[15px] text-[#007AFF]" />
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <p className="text-[15px] font-semibold text-black truncate">
                      Job #{booking.booking_number} - {booking.booking_title}
                    </p>
                    <p className="text-[13px] text-gray-500 mt-0.5">
                      {booking.totalHours.toFixed(1)}h · ${booking.totalEarnings.toFixed(2)}
                      {booking.end_date && ` · ${format(parseISO(booking.end_date), 'MMM d')}`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRequestFlatRateEntries(bookingId, booking.booking_number, booking.booking_title);
                    }}
                    className="text-[13px] font-medium text-[#007AFF] px-2.5 py-1.5 rounded-lg active:bg-blue-50 transition-colors"
                  >
                    + Add
                  </button>
                  <ChevronDown className={`h-4 w-4 text-gray-300 transition-transform ${
                    expandedBookings.has(bookingId) ? 'rotate-180' : ''
                  }`} />
                </div>
              </button>
              
              {expandedBookings.has(bookingId) && (
                <div className="px-4 pb-4 space-y-2">
                  {booking.items.map((item) => (
                    <div key={item.id} className="bg-[#F2F2F7] rounded-xl p-3.5">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-[15px] font-medium text-black truncate">{item.item_name}</p>
                          {item.item_description && (
                            <p className="text-[12px] text-gray-400 mt-0.5 line-clamp-1">{item.item_description}</p>
                          )}
                        </div>
                        <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded-md ml-2 ${
                          item.process_status === 'PAID'
                            ? 'bg-green-100 text-green-700'
                            : item.process_status === 'VOIDED'
                              ? 'bg-red-50 text-red-700'
                              : 'bg-orange-100 text-orange-600'
                        }`}>
                          {item.process_status === 'PAID' ? 'PAID' : item.process_status === 'VOIDED' ? 'VOIDED' : 'UNPAID'}
                        </span>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 text-[13px] text-gray-500">
                          <span>{formatDate(item.completed_at || item.created_at)}</span>
                          {item.entry_type === 'flat_rate' && <span>{item.hours.toFixed(1)}h</span>}
                          <span>
                            {item.entry_type === 'commission' 
                              ? 'Commission'
                              : `$${item.compensation_type === 'flat_rate' 
                                ? item.flat_rate_amount?.toFixed(2)
                                : item.hourly_rate?.toFixed(2)
                              }/hr`
                            }
                          </span>
                        </div>
                        <span className="text-[15px] font-semibold text-black">
                          ${item.earnings?.toFixed(2) || '0.00'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}

          {/* Empty state */}
          {entries.length === 0 && (
            <div className="text-center py-10 px-6">
              <div className="h-12 w-12 rounded-full bg-[#F2F2F7] flex items-center justify-center mx-auto mb-3">
                <DollarSign className="h-6 w-6 text-gray-400" />
              </div>
              <h4 className="text-[17px] font-semibold text-black mb-1">No Earnings</h4>
              <p className="text-[15px] text-gray-500 max-w-[280px] mx-auto">
                No earnings entries found for this time period.
              </p>
            </div>
          )}
        </div>
      )}

      <PayrollChangeRequestModal
        isOpen={isChangeRequestModalOpen}
        onClose={handleCloseChangeRequestModal}
        entry={requestingEntry}
        onSubmit={handleSubmitChangeRequest}
      />

      <AddMissingEntryModal
        isOpen={isAddMissingEntryModalOpen}
        onClose={handleCloseAddMissingEntryModal}
        resourceId={userInfo.resourceId || ''}
        businessId={selectedBusiness?.id || ''}
        onSuccess={handleAddMissingEntrySuccess}
      />

      {requestingBooking && (
        <RequestFlatRateEntriesModal
          isOpen={isFlatRateRequestModalOpen}
          onClose={handleCloseFlatRateRequestModal}
          bookingId={requestingBooking.id}
          bookingNumber={requestingBooking.number}
          bookingTitle={requestingBooking.title}
          businessId={selectedBusiness?.id || ''}
          resourceId={userInfo?.resourceId || ''}
          onSuccess={handleFlatRateRequestSuccess}
        />
      )}
    </div>
  );
} 